'use client';

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  Organization,
  fetchMyOrganizations,
} from '@/lib/organization-api';
import {
  getActiveOrganizationId,
  saveActiveOrganizationId,
} from '@/lib/api';
import { useAuth } from './AuthProvider';

type OrganizationContextValue = {
  organizations: Organization[];
  currentOrganization: Organization | null;
  currentOrganizationId: number | 'all';
  isAllOrganizations: boolean;
  primaryColor: string;
  loading: boolean;
  setCurrentOrganizationId: (id: number | string | 'all') => void;
  refreshOrganizations: () => Promise<Organization[]>;
};

const OrganizationContext = createContext<OrganizationContextValue | null>(null);

const DEFAULT_PRIMARY_COLOR = '#C0392B';
const DEFAULT_SECONDARY_COLOR = '#FEF0ED';

function applyBrandingTheme(org: Organization | null) {
  if (typeof document === 'undefined') return;
  if (typeof window !== 'undefined' && window.location.pathname.startsWith('/read/')) {
    return;
  }

  const root = document.documentElement;
  if (org && org.primary_color) {
    root.style.setProperty('--primary', org.primary_color);
    root.style.setProperty('--ring', org.primary_color);
    if (org.secondary_color) {
      root.style.setProperty('--secondary', org.secondary_color);
    }
  } else {
    root.style.setProperty('--primary', DEFAULT_PRIMARY_COLOR);
    root.style.setProperty('--ring', DEFAULT_PRIMARY_COLOR);
    root.style.setProperty('--secondary', DEFAULT_SECONDARY_COLOR);
  }
}

export function OrganizationProvider({ children }: { children: React.ReactNode }) {
  const { user, status } = useAuth();
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [selectedOrgId, setSelectedOrgId] = useState<number | 'all'>('all');
  const [loading, setLoading] = useState(true);

  const isSuperAdmin = user?.role?.slug === 'super_admin';

  const refreshOrganizations = useCallback(async () => {
    if (status !== 'authenticated' || !user) {
      setOrganizations([]);
      setLoading(false);
      return [];
    }

    try {
      setLoading(true);
      const list = await fetchMyOrganizations();
      setOrganizations(list);
      return list;
    } catch {
      setOrganizations([]);
      return [];
    } finally {
      setLoading(false);
    }
  }, [status, user]);

  useEffect(() => {
    if (status === 'authenticated' && user) {
      void refreshOrganizations();
    } else {
      setOrganizations([]);
      setSelectedOrgId('all');
      applyBrandingTheme(null);
      setLoading(false);
    }
  }, [status, user, refreshOrganizations]);

  // Determine current active organization and auto-selection
  useEffect(() => {
    if (status !== 'authenticated' || !user || organizations.length === 0) {
      return;
    }

    const saved = getActiveOrganizationId();

    // Multi-organization user (super admin or admin with >1 orgs)
    if (isSuperAdmin || organizations.length > 1) {
      if (saved && saved !== 'all') {
        const found = organizations.find((o) => String(o.id) === String(saved));
        if (found) {
          setSelectedOrgId(found.id);
          applyBrandingTheme(found);
          return;
        }
      }
      setSelectedOrgId('all');
      saveActiveOrganizationId('all');
      applyBrandingTheme(null);
      return;
    }

    // Single organization user
    if (organizations.length === 1) {
      const singleOrg = organizations[0];
      setSelectedOrgId(singleOrg.id);
      saveActiveOrganizationId(singleOrg.id);
      applyBrandingTheme(singleOrg);
      return;
    }
  }, [status, user, organizations, isSuperAdmin]);

  const setCurrentOrganizationId = useCallback(
    (id: number | string | 'all') => {
      if (id === 'all') {
        if (!isSuperAdmin && organizations.length <= 1) return;
        setSelectedOrgId('all');
        saveActiveOrganizationId('all');
        applyBrandingTheme(null);
        return;
      }

      const numId = Number(id);
      const found = organizations.find((o) => o.id === numId);
      if (found) {
        setSelectedOrgId(numId);
        saveActiveOrganizationId(numId);
        applyBrandingTheme(found);
      }
    },
    [organizations, isSuperAdmin]
  );

  const currentOrganization = useMemo(() => {
    if (selectedOrgId === 'all') return null;
    return organizations.find((o) => o.id === selectedOrgId) || null;
  }, [organizations, selectedOrgId]);

  const primaryColor = currentOrganization?.primary_color || DEFAULT_PRIMARY_COLOR;

  const value = useMemo<OrganizationContextValue>(
    () => ({
      organizations,
      currentOrganization,
      currentOrganizationId: selectedOrgId,
      isAllOrganizations: selectedOrgId === 'all',
      primaryColor,
      loading,
      setCurrentOrganizationId,
      refreshOrganizations,
    }),
    [
      organizations,
      currentOrganization,
      selectedOrgId,
      primaryColor,
      loading,
      setCurrentOrganizationId,
      refreshOrganizations,
    ]
  );

  return (
    <OrganizationContext.Provider value={value}>
      {children}
    </OrganizationContext.Provider>
  );
}

export function useOrganization() {
  const context = useContext(OrganizationContext);
  if (!context) {
    throw new Error('useOrganization must be used within an OrganizationProvider');
  }
  return context;
}
